import { ApprovalProcessQuery } from '../models/approval-flow.model';
import { APP_NAVIGATION } from './app-navigation.config';

/**
 * Constantes del flujo de aprobación — FUENTE ÚNICA.
 *
 * El API de aprobación es genérico y lo comparten varios sistemas de Reprico; lo que
 * distingue a los flujos de RBI es el `processName`, que se acordó con el equipo que
 * opera el API. No existe un `processType`: Gabriel lo tuvo en una versión anterior y lo
 * renombró a `processName`, y los `GET` solo filtran por ese campo.
 */

/**
 * `processName` de los flujos de las ofertas de ROE. Lo fija el equipo que opera el API:
 * un proceso por tipo de trámite, no uno por sistema.
 */
export const ROE_OFFERS_PROCESS_NAME = 'roe-offers';

/**
 * `processName` de los pedidos automáticos HS, uno por tipo de pedido. Los aprobadores los
 * pone el API (`defaultApprovers`), así que RBI no los manda.
 *
 * Vive aquí porque la bandeja y la campanita necesitan reconocer el proceso de un flujo sin
 * saber nada de pedidos. Qué tipo corresponde a cuál lo resuelve el feature contra el
 * catálogo del API (`procesoDeAprobacion`), no un número escrito a mano.
 */
export const HS_ORDER_PROCESS_NAMES = {
    reprico: 'sbl-hs-reprico-order',
    vitamins: 'sbl-hs-vitamins-order',
} as const;

/**
 * Esquema de identidad que el consumidor `rbi` declaró al darse de alta. Un esquema no
 * declarado se rechaza con `403 actor.scheme-not-allowed`.
 */
export const APPROVAL_IDENTITY_SCHEME = 'email';

/**
 * Procesos cuyos aprobadores por defecto se gestionan a mano desde RBI: los que ofrecen el formulario
 * de usuario y el diálogo «Aprobadores por proceso». Los automáticos no se listan ahí.
 */
export const MANUAL_PROCESSES_QUERY: ApprovalProcessQuery = { managementMode: 'Manual' };

/** Tope de página que acepta el API. Pedir más responde `400 request.invalid`. */
export const APPROVAL_FLOW_MAX_PAGE_SIZE = 100;

/**
 * Cuántos pendientes publica la fuente de aprobaciones en la bandeja de notificaciones.
 * Es un aviso, no la bandeja completa: todo se ve en «Centro de aprobaciones › Aprobaciones».
 */
export const APPROVAL_FLOW_NOTIFICATIONS_SIZE = 20;

/**
 * Identificador canónico de un usuario para el API (`esquema:valor`).
 * El API normaliza a minúsculas, así que se envía ya normalizado.
 */
export function toPrincipalId(email: string): string {
    return `${APPROVAL_IDENTITY_SCHEME}:${email.trim().toLowerCase()}`;
}

/** El correo de un `principalId`; '' si no tiene la forma esperada. */
export function emailFromPrincipalId(principalId: string): string {
    const prefijo = `${APPROVAL_IDENTITY_SCHEME}:`;
    const valor = String(principalId ?? '').trim().toLowerCase();
    return valor.startsWith(prefijo) ? valor.slice(prefijo.length) : '';
}

/** Busca el enlace declarado de una página en el menú; el literal es solo respaldo. */
function rutaDeNavegacion(moduleIdentifier: string, pageIdentifier: string, respaldo: string): string {
    return (
        APP_NAVIGATION.find(
            (entrada) => entrada.moduleIdentifier === moduleIdentifier && entrada.pageIdentifier === pageIdentifier,
        )?.routerLink ?? respaldo
    );
}

/**
 * Bandeja de aprobaciones. FUENTE ÚNICA: APP_NAVIGATION.
 *
 * La usan la campanita y los avisos de pedidos y ofertas: como sale del menú, mover la
 * bandeja de módulo no deja ningún enlace apuntando al sitio viejo.
 */
export const APPROVALS_INBOX_ROUTE = rutaDeNavegacion(
    'centro_aprobaciones',
    'aprobaciones',
    '/centro-aprobaciones/configuracion/aprobaciones',
);

/** Listado de ofertas. FUENTE ÚNICA: APP_NAVIGATION. */
const OFERTAS_LIST_ROUTE = rutaDeNavegacion('comercial', 'ofertas', '/comercial/configuracion/ofertas');

/** Prefijo de los identificadores de oferta de ROE. */
const OFFER_ID_PREFIX = 'OF-';

/**
 * Devuelve el id de la oferta tal como lo espera ROE.
 *
 * Hace falta porque el API de aprobación **normaliza el `processId` a minúsculas** al
 * guardarlo, y lo devuelve así en las bandejas; ROE, en cambio, sí distingue mayúsculas:
 * con `of-…` responde 200 pero con `found: false`, que es peor que un 404 porque no
 * parece un error. Solo se repone el prefijo: el resto del id ya es minúsculo en origen.
 */
export function toOfferId(processId: string): string {
    const valor = sinVersion(String(processId ?? '').trim());
    return valor.toLowerCase().startsWith(OFFER_ID_PREFIX.toLowerCase())
        ? OFFER_ID_PREFIX + valor.slice(OFFER_ID_PREFIX.length)
        : valor;
}

/**
 * Lo que separa la oferta de su versión dentro de la referencia del flujo: `OF-02:3`.
 *
 * Una oferta de ROE es inmutable: editarla no la cambia, crea una **versión nueva**, y cada
 * versión se aprueba por su cuenta. Por eso el flujo no puede identificarse solo por la
 * oferta —si no, la versión nueva se encontraría con el flujo de la anterior, ya decidido, y
 * se quedaría sin forma de pedir su propia aprobación—.
 *
 * Queda `OF-02:3`. Un identificador de ROE es un UUID, que nunca lleva dos puntos, así que
 * no hay forma de confundir el separador con parte del id.
 */
const SEPARADOR_VERSION = ':';

/**
 * Separadores que se reconocen al leer una referencia.
 *
 * El primero es el que se escribe hoy. Los otros se usaron antes: los flujos creados así
 * siguen entendiéndose, que es más barato que migrarlos. El orden importa —`:` a secas
 * también casa con el principio de `:v`—, y por eso cada candidato solo vale si lo que
 * sigue es un entero positivo.
 */
const SEPARADORES_CONOCIDOS: readonly string[] = [SEPARADOR_VERSION, '-v', ':v'];

/** La referencia del flujo de una versión concreta de una oferta: `OF-02` + 3 → `OF-02:3`. */
export function offerFlowReference(offerId: string, version: number): string {
    const id = String(offerId ?? '').trim();
    return Number.isFinite(version) && version > 0 ? `${id}${SEPARADOR_VERSION}${Math.trunc(version)}` : id;
}

/**
 * La versión que lleva una referencia de flujo; `null` si no lleva ninguna.
 *
 * Los flujos abiertos antes de esto no la llevan: se reconocen por eso, y se tratan como lo
 * que son, aprobaciones de una oferta sin versión declarada.
 */
export function offerFlowVersion(processId: string): number | null {
    return corteDeVersion(String(processId ?? '').trim())?.version ?? null;
}

/** La referencia sin su parte de versión. */
function sinVersion(valor: string): string {
    const corte = corteDeVersion(valor);
    return corte ? valor.slice(0, corte.desde) : valor;
}

/**
 * Dónde empieza la versión dentro de la referencia y cuál es; `null` si no lleva.
 *
 * Se prueba cada separador conocido y se exige que lo que sigue sea un entero positivo: así
 * un id que casualmente contenga el separador no se confunde con una versión.
 */
function corteDeVersion(valor: string): { desde: number; version: number } | null {
    for (const separador of SEPARADORES_CONOCIDOS) {
        const desde = valor.toLowerCase().lastIndexOf(separador);
        if (desde < 0) {
            continue;
        }
        const version = Number(valor.slice(desde + separador.length));
        if (Number.isInteger(version) && version > 0) {
            return { desde, version };
        }
    }
    return null;
}

/**
 * Parámetro con el que el listado de ofertas abre una ficha concreta. Es la forma de
 * **ver** una oferta sin entrar a editarla, que es lo que necesita un aprobador.
 */
export const FICHA_QUERY_PARAM = 'ficha';

/** Enlace para VER la ficha de una oferta (solo lectura). */
export function offerViewLink(offerId: string): { commands: string[]; queryParams: Record<string, string> } {
    return {
        commands: [OFERTAS_LIST_ROUTE],
        queryParams: { [FICHA_QUERY_PARAM]: toOfferId(offerId) },
    };
}

/** Comandos de `Router.navigate` para EDITAR una oferta. */
export function offerEditRoute(offerId: string): string[] {
    return [OFERTAS_LIST_ROUTE, toOfferId(offerId), 'editar'];
}
